<template>
  <div class="claim-start-shell sba-slide-in">
    <div class="claim-start-panel">
      <section class="claim-start-section">
        <div class="claim-start-section-head">
          <h6>Provider</h6>
          <i class="material-icons claim-start-check" v-if="selectedProvider">check_circle</i>
        </div>
        <div class="claim-start-field">
          <div class="input-field">
            <FilterSimpleDiv inputId="filterProvider" fieldName="Select Provider" :data="filterProviderData"
              :search="true" :appendDropdownToBody="true" :forceDropdownDown="true"
              dropdownClass="claim-start-dropdown" @selected="handlePick" @searching="handleSearch" />
          </div>
        </div>
      </section>

      <section class="claim-start-section">
        <div class="claim-start-section-head">
          <h6>Period</h6>
          <i class="material-icons claim-start-check" v-if="selectedDurationMonth && selectedDurationYear">check_circle</i>
        </div>
        <div class="claim-start-grid">
          <div class="input-field">
            <FilterSimpleDiv inputId="filterDurationMonth" fieldName="Select Month" :data="filterDurationMonthData"
              :search="false" :appendDropdownToBody="true" :forceDropdownDown="true"
              dropdownClass="claim-start-dropdown" @selected="handlePick" @searching="handleSearch" />
          </div>
          <div class="input-field">
            <FilterSimpleDiv inputId="filterDurationYear" fieldName="Select Year" :data="filterDurationYearData"
              :search="false" :appendDropdownToBody="true" :forceDropdownDown="true"
              dropdownClass="claim-start-dropdown" @selected="handlePick" @searching="handleSearch" />
          </div>
        </div>
      </section>

      <section class="claim-start-section">
        <div class="claim-start-section-head">
          <h6>Scheme</h6>
          <i class="material-icons claim-start-check" v-if="selectedScheme">check_circle</i>
        </div>
        <div class="claim-start-field">
          <div class="input-field">
            <FilterSimpleDiv inputId="filterScheme" fieldName="Select Scheme" :data="filterSchemeData" :search="false"
              :appendDropdownToBody="true" :forceDropdownDown="true" dropdownClass="claim-start-dropdown"
              @selected="handlePick" @searching="handleSearch" />
          </div>
        </div>
      </section>

      <div class="claim-start-submit-row">
        <button class="claim-start-submit" :class="{
          disabled:
            !selectedProvider ||
            !selectedDurationMonth ||
            !selectedDurationYear ||
            !selectedScheme ||
            isSubmitting,
        }" @click="handleSubmit">
          <span>{{ isSubmitting ? "Processing ..." : "Get Started" }}</span>
          <i class="material-icons">{{
            isSubmitting ? "hourglass_empty" : "chevron_right"
          }}</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from "vue";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import FilterSimpleDiv from "@/components/search/FilterSimpleDiv.vue";
import {
  debounce,
  getCachedServerDate,
  loadCachedProviderFilter,
} from "@/composables/requestOptimization";
import { claimCap } from "@/laravel/config";

export default {
  name: "StartClaimComponent",
  components: { FilterSimpleDiv },
  emits: ["next"],
  setup(props, context) {
    const isSubmitting = ref(false);
    const error = ref(null);
    const responseSearch = ref(null);
    const responseSchemeDefault = ref(null);
    const responseDurationYearDefault = ref(null);
    const responseDurationMonthDefault = ref(null);
    const selectedItem = ref(null);
    const selectedProvider = ref(null);
    const selectedDurationYear = ref(null);
    const selectedDurationMonth = ref(null);
    const selectedScheme = ref(null);
    const period = ref(null);
    const scheme = ref(null);
    const claimYearCap = Math.max(1, Number(claimCap) || 3);
    let providerFilterRequestId = 0;

    // store
    const store = useStore();

    // Provider
    const filterProviderData = computed(() => {
      // start
      let val = null;

      let valPlus;

      if (responseSearch.value) {
        if (responseSearch.value.key == "filterProvider") {
          valPlus = responseSearch.value.value.reduce((newArr, x) => {
            newArr[x.name] = null;
            return newArr;
          }, {});
          val = { ...val, ...valPlus };
        }
      }

      // return final
      return val;
    });

    // Scheme
    const filterSchemeData = computed(() => {
      // start
      let val = null;

      let valPlus;

      if (responseSchemeDefault.value) {
        if (responseSchemeDefault.value.key == "filterScheme") {
          valPlus = responseSchemeDefault.value.value.reduce((newArr, x) => {
            newArr[x.name] = null;
            return newArr;
          }, {});
          val = { ...val, ...valPlus };
        }
      }

      // return final
      return val;
    });

    // Duration (Month)
    const filterDurationMonthData = computed(() => {
      // start
      let val = null;

      let valPlus;

      if (responseDurationMonthDefault.value) {
        if (responseDurationMonthDefault.value.key == "filterDurationMonth") {
          valPlus = responseDurationMonthDefault.value.value.reduce(
            (newArr, x) => {
              newArr[x.period] = null;
              return newArr;
            },
            {}
          );
          val = { ...val, ...valPlus };
        }
      }

      // return final
      return val;
    });

    // Duration (Year)
    const filterDurationYearData = computed(() => {
      // start
      let val = null;

      let valPlus;

      if (responseDurationYearDefault.value) {
        if (responseDurationYearDefault.value.key == "filterDurationYear") {
          valPlus = responseDurationYearDefault.value.value.reduce(
            (newArr, x) => {
              newArr[x.period] = null;
              return newArr;
            },
            {}
          );
          val = { ...val, ...valPlus };
        }
      }

      // return final
      return val;
    });

    // filter (Initialize)
    onMounted(() => {
      // initialize filterDuration
      let val = [
        { period: "January", month: 1 },
        { period: "February", month: 2 },
        { period: "March", month: 3 },
        { period: "April", month: 4 },
        { period: "May", month: 5 },
        { period: "June", month: 6 },
        { period: "July", month: 7 },
        { period: "August", month: 8 },
        { period: "September", month: 9 },
        { period: "October", month: 10 },
        { period: "November", month: 11 },
        { period: "December", month: 12 },
      ];
      // set
      responseDurationMonthDefault.value = {
        key: "filterDurationMonth",
        value: val,
      };
    });

    // filter (Initialize)
    onMounted(() => {
      // set
      // load value from search
      getSearchItemsFromApi("operator/server/date", "filterDurationYear");
      // initialize filterDurationYear
      responseDurationYearDefault.value = {
        key: "filterDurationYear",
        value: [],
      };
    });

    // search function
    const getSearchItemsFromApi = (table, key) => {
      (async () => {
        const { res } = table === "operator/server/date"
          ? await getCachedServerDate(getItems)
          : await getItems(table);
        if (res.value) {
          if (key == "filterScheme") {
            // one time
            responseSchemeDefault.value = { key: key, value: res.value.data };
          } else if (key == "filterDurationYear") {
            // one time
            responseDurationYearDefault.value = {
              key: key,
              value: Array.from({ length: claimYearCap }, (_, index) => {
                const year = res.value.data.current_year - claimYearCap + index + 1;
                return { period: year, year: year };
              }),
            };
          } else {
            // search
            responseSearch.value = { key: key, value: res.value.data };
          }
        }
      })();
    };

    const loadProviderFilterSearch = debounce(async (query, requestId) => {
      await loadCachedProviderFilter({
        getItems,
        query,
        requestId,
        currentRequestId: () => providerFilterRequestId,
        onResult: (payload) => {
          responseSearch.value = payload;
        },
      });
    });

    // field filter (Initialize)
    onMounted(() => {
      // initialize filterScheme
      // getSearchItemsFromApi("scheme/filter", "filterScheme");
      responseSchemeDefault.value = { key: "filterScheme", value: store.state.user.schemes };
    });

    // filter components where search is set to "true"
    const handleSearch = (val) => {
      if (val.id == "filterProvider") {
        const query = val.value?.trim() || "";
        providerFilterRequestId++;

        if (query.length < 2) {
          responseSearch.value = { key: val.id, value: [] };
          return;
        }

        // get new serach items
        loadProviderFilterSearch(query, providerFilterRequestId);
      }
    };

    const handleSubmit = () => {
      // start
      isSubmitting.value = true;

      if (
        !selectedProvider.value ||
        !selectedDurationMonth.value ||
        !selectedDurationYear.value ||
        !selectedScheme.value
      ) {
        error.value = "All fields not selected"; // most unlikely
        // end
        isSubmitting.value = false;
      } else {
        setTimeout(function () {
          context.emit("next", {
            provider: selectedProvider.value,
            month: selectedDurationMonth.value,
            year: selectedDurationYear.value,
            scheme: selectedScheme.value,
          });
          // end
          isSubmitting.value = false;
        }, 2000);
      }
    };

    // get option
    const handlePick = (val) => {
      selectedItem.value = val;
      // watchEffect will take over from here
    };

    // search watcher
    watchEffect(() => {
      // start
      if (selectedItem.value) {
        // sort
        if (selectedItem.value.id == "filterProvider") {
          //set
          if (!selectedItem.value.value) {
            selectedProvider.value = null;
          } else {
            responseSearch.value.value.forEach((x) => {
              if (x.name == selectedItem.value.value) {
                // found
                selectedProvider.value = { name: x.name, search: x.id };
              }
            });
          }
        } else if (selectedItem.value.id == "filterScheme") {
          //set
          if (!selectedItem.value.value) {
            selectedScheme.value = null;
          } else {
            responseSchemeDefault.value.value.forEach((x) => {
              if (x.name == selectedItem.value.value) {
                // found
                selectedScheme.value = { name: x.name, search: x.id };
              }
            });
          }
        } else if (selectedItem.value.id == "filterDurationMonth") {
          //set
          if (!selectedItem.value.value) {
            selectedDurationMonth.value = null;
          } else {
            responseDurationMonthDefault.value.value.forEach((x) => {
              if (x.period == selectedItem.value.value) {
                // found
                selectedDurationMonth.value = {
                  name: x.period,
                  search: x.month,
                };
              }
            });
          }
        } else if (selectedItem.value.id == "filterDurationYear") {
          //set
          if (!selectedItem.value.value) {
            selectedDurationYear.value = null;
          } else {
            responseDurationYearDefault.value.value.forEach((x) => {
              if (x.period == selectedItem.value.value) {
                // found
                selectedDurationYear.value = { name: x.period, search: x.year };
              }
            });
          }
        }
      }
    });

    return {
      isSubmitting,
      error,
      handleSubmit,
      handleSearch,
      handlePick,
      selectedItem,
      filterProviderData,
      filterSchemeData,
      filterDurationMonthData,
      filterDurationYearData,
      selectedProvider,
      selectedScheme,
      selectedDurationMonth,
      selectedDurationYear,
      period,
      scheme,
    };
  },
};
</script>

<style scoped>
.claim-start-shell {
  padding: 0;
}

.claim-start-panel,
.claim-start-section {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.claim-start-panel {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  overflow: hidden;
}

.claim-start-section {
  border-bottom: 1px solid #e1e8eb;
  padding: 20px 24px 12px;
}

.claim-start-section-head {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.claim-start-section-head h6 {
  color: #263238;
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0;
}

.claim-start-check {
  color: #00897b;
  font-size: 19px;
  margin-left: auto;
}

.claim-start-field,
.claim-start-grid {
  padding-left: 0;
}

.claim-start-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.72fr);
}

.claim-start-submit-row {
  align-items: center;
  background: #f8fafb;
  display: flex;
  justify-content: flex-end;
  padding: 14px 24px;
}

.claim-start-submit {
  align-items: center;
  background: #00695c;
  border: 1px solid #004d40;
  border-radius: 6px;
  box-shadow: 0 10px 18px -14px rgba(0, 77, 64, 0.9);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 10px;
  justify-content: center;
  min-height: 42px;
  min-width: 150px;
  padding: 0 18px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
}

.claim-start-submit:hover {
  background: #004d40;
  border-color: #004d40;
}

.claim-start-submit.disabled {
  cursor: default;
  opacity: 0.52;
  pointer-events: none;
}

.claim-start-submit i {
  font-size: 18px;
  line-height: 18px;
}

:global(.claim-start-dropdown.autocomplete-content.dropdown-content) {
  max-height: 320px;
  overflow-y: auto;
  z-index: 1200;
}

:global(.claim-start-dropdown.autocomplete-content.dropdown-content li > span) {
  color: #00897b;
  font-size: 1rem;
}

@media only screen and (max-width: 700px) {
  .claim-start-section {
    padding: 18px 18px 10px;
  }

  .claim-start-field,
  .claim-start-grid {
    padding-left: 0;
  }

  .claim-start-grid {
    gap: 0;
    grid-template-columns: 1fr;
  }

  .claim-start-submit-row {
    align-items: stretch;
    padding: 14px 18px;
  }

  .claim-start-submit {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .claim-start-panel,
  .claim-start-section {
    animation: none;
  }
}
</style>
